import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  User,
  Stethoscope,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  isSameDay,
  isToday,
  isBefore,
  startOfDay,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import KioskLayout from "@/components/KioskLayout";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function KioskReschedule() {
  const navigate = useNavigate();
  const { appointmentId } = useParams();

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [appointmentPlanType, setAppointmentPlanType] = useState(null);

  const [appointment, setAppointment] = useState(null);
  const [patient, setPatient] = useState(null);

  const [specialties, setSpecialties] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(true);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);

  const [professionals, setProfessionals] = useState([]);
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);

  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));

  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showConvenioWarning, setShowConvenioWarning] = useState(false);
  const [convenioWarningMessage, setConvenioWarningMessage] = useState(
    "Este médico não atende o convênio vinculado ao seu cadastro."
  );
  const [fallbackLoading, setFallbackLoading] = useState(false);

  const getProfessionalId = (professional) =>
    professional?.id || professional?.profissional_id;

  const getProfessionalName = (professional) =>
    professional?.nome || professional?.name || "Profissional";

  const getSpecialtyId = (specialty) =>
    specialty?.id || specialty?.especialidade_id;

  const getSpecialtyName = (specialty) =>
    specialty?.name || specialty?.descricao || specialty?.nome || "Especialidade";

  const isConvenioFlow = appointmentPlanType === "CONVENIO";
  const isRetornoFlow = appointmentPlanType === "RETORNO";

  const getCurrentProcedureId = () => {
    if (isRetornoFlow) return 6;
    return 100;
  };

  const getPatientInsuranceId = () =>
    patient?.convenio_id ||
    patient?.convenios?.[0]?.convenio_id ||
    patient?.convenio?.id ||
    patient?.convenio?.convenio_id ||
    patient?.insurance_id ||
    patient?.raw?.convenio_id ||
    patient?.raw?.convenios?.[0]?.convenio_id ||
    patient?.raw?.convenio?.id ||
    patient?.raw?.convenio?.convenio_id ||
    null;

  const getPatientInsurancePlanId = () =>
    patient?.convenio_plano_id ??
    patient?.convenios?.[0]?.plano_id ??
    patient?.convenio?.plano_id ??
    patient?.convenio?.convenio_plano_id ??
    patient?.plano_id ??
    patient?.insurance_plan_id ??
    patient?.raw?.convenio_plano_id ??
    patient?.raw?.convenios?.[0]?.plano_id ??
    patient?.raw?.convenio?.plano_id ??
    patient?.raw?.convenio?.convenio_plano_id ??
    0;

  const fetchSpecialties = async () => {
    setLoadingSpecialties(true);

    try {
      const response = await axios.get(`${API_URL}/api/specialties`);

      if (response.data.success) {
        setSpecialties(response.data.specialties || []);
      } else {
        setSpecialties([]);
      }
    } catch (error) {
      console.error("Error fetching specialties:", error);
      toast.error("Erro ao carregar especialidades. Por favor, tente novamente.");
    } finally {
      setLoadingSpecialties(false);
    }
  };

  const fetchProfessionals = async (specialty) => {
    const specialtyId = getSpecialtyId(specialty);

    if (!specialtyId) {
      toast.error("Especialidade inválida.");
      return;
    }

    setLoadingProfessionals(true);
    setSelectedSpecialty(specialty);
    setSelectedProfessional(null);
    setAvailableDates([]);
    setSelectedDate(null);
    setAvailableSlots([]);
    setSelectedTime(null);
    setCurrentMonth(startOfMonth(new Date()));
    setStep(2);

    try {
      const response = await axios.get(`${API_URL}/api/professionals`, {
        params: { specialty_id: specialtyId },
      });

      if (response.data.success) {
        setProfessionals(response.data.professionals || []);
      } else {
        setProfessionals([]);
      }
    } catch (error) {
      console.error("Error fetching professionals:", error);
      toast.error("Erro ao carregar médicos. Por favor, tente novamente.");
    } finally {
      setLoadingProfessionals(false);
    }
  };

  const fetchAvailableDatesForMonth = async (monthDate) => {
    if (!selectedProfessional) return;

    const professionalId = getProfessionalId(selectedProfessional);
    const specialtyId = getSpecialtyId(selectedSpecialty);

    const start = format(startOfMonth(monthDate), "dd-MM-yyyy");
    const end = format(endOfMonth(monthDate), "dd-MM-yyyy");

    setLoadingDates(true);

    try {
      const params = {
        specialty_id: specialtyId,
        date_start: start,
        date_end: end,
      };

      if (!appointment) {
        params.procedure_id = getCurrentProcedureId();
      }

      // Não filtramos disponibilidade por convênio neste fluxo.
      // A validação do convênio acontece somente na confirmação.

      const response = await axios.get(
        `${API_URL}/api/professionals/${professionalId}/available-dates`,
        { params }
      );

      if (response.data.success) {
        setAvailableDates(response.data.dates || []);
      } else {
        setAvailableDates([]);
      }
    } catch (error) {
      console.error("Error fetching month dates:", error);
      toast.error("Erro ao carregar datas deste mês.");
    } finally {
      setLoadingDates(false);
    }
  };

  const fetchAvailableDates = async (professional) => {
    const professionalId = getProfessionalId(professional);
    const specialtyId = getSpecialtyId(selectedSpecialty);

    if (!professionalId) {
      toast.error("Profissional inválido.");
      return;
    }

    setLoadingDates(true);
    setSelectedProfessional(professional);
    setAvailableDates([]);
    setSelectedDate(null);
    setAvailableSlots([]);
    setSelectedTime(null);
    setCurrentMonth(startOfMonth(new Date()));
    setStep(appointment ? 3 : 4);

    try {
      const params = {
        specialty_id: specialtyId,
      };

      if (!appointment) {
        params.procedure_id = getCurrentProcedureId();
      }

      // Não filtramos disponibilidade por convênio neste fluxo.
      // A validação do convênio acontece somente na confirmação.

      const response = await axios.get(
        `${API_URL}/api/professionals/${professionalId}/available-dates`,
        { params }
      );

      if (response.data.success) {
        setAvailableDates(response.data.dates || []);
      } else {
        setAvailableDates([]);
      }
    } catch (error) {
      console.error("Error fetching available dates:", error);
      toast.error("Erro ao carregar datas disponíveis.");
    } finally {
      setLoadingDates(false);
    }
  };

  const fetchAvailableDatesByContext = async (professional, specialty) => {
    const professionalId = getProfessionalId(professional);
    const specialtyId = getSpecialtyId(specialty);

    if (!professionalId) {
      toast.error("Profissional inválido.");
      return;
    }

    setLoadingDates(true);
    setSelectedProfessional(professional);
    setSelectedSpecialty(specialty);
    setAvailableDates([]);
    setSelectedDate(null);
    setAvailableSlots([]);
    setSelectedTime(null);
    setCurrentMonth(startOfMonth(new Date()));
    setStep(3);

    try {
      const response = await axios.get(
        `${API_URL}/api/professionals/${professionalId}/available-dates`,
        {
          params: { specialty_id: specialtyId },
        }
      );

      if (response.data.success) {
        setAvailableDates(response.data.dates || []);
      } else {
        setAvailableDates([]);
      }
    } catch (error) {
      console.error("Error fetching available dates:", error);
      toast.error("Erro ao carregar datas disponíveis.");
    } finally {
      setLoadingDates(false);
    }
  };

  useEffect(() => {
    const storedPatient = sessionStorage.getItem("patient");
    const storedAppointment = sessionStorage.getItem("selectedAppointment");

    if (!storedPatient) {
      toast.error("Sessão expirada. Por favor, identifique-se novamente.");
      navigate("/identify/reschedule");
      return;
    }

    const parsedPatient = JSON.parse(storedPatient);
    setPatient(parsedPatient);

    if (appointmentId === "new") {
      const hasInsurance =
        parsedPatient?.convenio_id ||
        parsedPatient?.convenios?.[0]?.convenio_id ||
        parsedPatient?.convenio?.id ||
        parsedPatient?.convenio?.convenio_id ||
        parsedPatient?.insurance_id ||
        parsedPatient?.raw?.convenio_id ||
        parsedPatient?.raw?.convenios?.[0]?.convenio_id ||
        parsedPatient?.raw?.convenio?.id ||
        parsedPatient?.raw?.convenio?.convenio_id;

      setAppointmentPlanType(hasInsurance ? "CONVENIO" : "PARTICULAR");
    }

    if (storedAppointment && appointmentId !== "new") {
      const parsedAppointment = JSON.parse(storedAppointment);
      setAppointment(parsedAppointment);

      const originalSpecialty = {
        id: parsedAppointment.specialty_id,
        especialidade_id: parsedAppointment.specialty_id,
        name: parsedAppointment.specialty_name || "Especialidade",
      };

      const originalProfessional = {
        id: parsedAppointment.professional_id,
        profissional_id: parsedAppointment.professional_id,
        nome: parsedAppointment.professional_name || "Profissional",
        name: parsedAppointment.professional_name || "Profissional",
      };

      setSelectedSpecialty(originalSpecialty);
      setSelectedProfessional(originalProfessional);
      setLoadingSpecialties(false);

      fetchAvailableDatesByContext(originalProfessional, originalSpecialty);
      return;
    }

    setAppointment(null);
    fetchSpecialties();
  }, [appointmentId, navigate]);

  const handleSelectSpecialty = async (specialty) => {
    await fetchProfessionals(specialty);
  };

  const handleSelectProfessional = async (professional) => {
    await fetchAvailableDates(professional);
  };

  const getSlotTime = (slot) => slot.horario || slot.hora || slot.time || "";

  const isVisibleSlot = (slot) => {
    const time = getSlotTime(slot);
    if (!time) return false;

    const hour = parseInt(time.slice(0, 2), 10);
    return hour < 21;
  };

  const sortSlots = (slots) => {
    return [...slots].sort((a, b) => {
      const timeA = getSlotTime(a);
      const timeB = getSlotTime(b);
      return timeA.localeCompare(timeB);
    });
  };

  const getGroupedSlots = () => {
    const visibleSortedSlots = sortSlots(
      (availableSlots || []).filter(isVisibleSlot)
    );

    return {
      morning: visibleSortedSlots.filter((slot) => {
        const hour = parseInt(getSlotTime(slot).slice(0, 2), 10);
        return hour >= 0 && hour < 12;
      }),
      afternoon: visibleSortedSlots.filter((slot) => {
        const hour = parseInt(getSlotTime(slot).slice(0, 2), 10);
        return hour >= 12 && hour < 18;
      }),
      evening: visibleSortedSlots.filter((slot) => {
        const hour = parseInt(getSlotTime(slot).slice(0, 2), 10);
        return hour >= 18 && hour < 21;
      }),
    };
  };

  const groupedSlots = getGroupedSlots();

  const handleSelectDate = async (date) => {
    if (!selectedProfessional) {
      toast.error("Selecione o médico.");
      return;
    }

    setSelectedDate(date);
    setLoadingSlots(true);
    setStep(appointment ? 4 : 5);
    setAvailableSlots([]);
    setSelectedTime(null);

    try {
      const professionalId = getProfessionalId(selectedProfessional);
      const dateStr = format(date, "dd-MM-yyyy");

      const params = {
        date: dateStr,
        specialty_id: getSpecialtyId(selectedSpecialty),
      };

      if (!appointment) {
        params.procedure_id = getCurrentProcedureId();
      }

      // Não filtramos disponibilidade por convênio neste fluxo.
      // A validação do convênio acontece somente na confirmação.

      const response = await axios.get(
        `${API_URL}/api/professionals/${professionalId}/available-slots`,
        { params }
      );

      if (response.data.success) {
        const sortedSlots = [...(response.data.slots || [])].sort((a, b) => {
          const timeA = getSlotTime(a);
          const timeB = getSlotTime(b);
          return timeA.localeCompare(timeB);
        });

        setAvailableSlots(sortedSlots);
      } else {
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error("Error fetching available slots:", error);
      toast.error("Erro ao carregar horários disponíveis.");
    } finally {
      setLoadingSlots(false);
    }
  };

  const selectedTimeData =
    availableSlots.find(
      (slot) => (slot.horario || slot.hora || slot.time) === selectedTime
    ) || null;

  const handleConfirm = async (forceParticular = false) => {
    if (!selectedDate || !selectedTime || !selectedProfessional || !patient) {
      toast.error("Selecione médico, data e horário.");
      return;
    }

    setLoading(true);

    try {
      let response;

      if (appointment) {
        response = await axios.post(`${API_URL}/api/appointments/reschedule`, {
          appointment_id: parseInt(appointmentId),
          patient_id: patient.id || patient.paciente_id,
          professional_id: getProfessionalId(selectedProfessional),
          specialty_id: getSpecialtyId(selectedSpecialty),
          local_id: selectedTimeData?.local_id
            ? parseInt(selectedTimeData.local_id)
            : null,
          new_date: format(selectedDate, "dd-MM-yyyy"),
          new_time: selectedTime,
          reason_id: 1,
          observation: "Remarcado pelo totem",
        });
      } else {
        const isConvenio = appointmentPlanType === "CONVENIO" && !forceParticular;
        const insuranceId = getPatientInsuranceId();
        const insurancePlanId = getPatientInsurancePlanId();

        if (isConvenio && !insuranceId) {
          toast.error("Convênio não encontrado no cadastro. Procure a recepção.");
          setLoading(false);
          return;
        }

        if (isConvenio) {
          const checkResponse = await axios.get(
			`${API_URL}/api/doctor-insurance-config/check`,
            {
              params: {
                professional_id: getProfessionalId(selectedProfessional),
                insurance_id: insuranceId,
              },
            }
          );

          const isAllowedByConfig = checkResponse.data?.allowed === true;

          if (!isAllowedByConfig) {
            setConvenioWarningMessage(
              checkResponse.data?.reason ||
                "Este médico não atende o convênio vinculado ao seu cadastro."
            );
            setShowConvenioWarning(true);
            setLoading(false);
            return;
          }
        }

        response = await axios.post(`${API_URL}/api/appointments/create`, {
          patient_id: patient.id || patient.paciente_id,
          professional_id: getProfessionalId(selectedProfessional),
          specialty_id: getSpecialtyId(selectedSpecialty),
          local_id: selectedTimeData?.local_id
            ? parseInt(selectedTimeData.local_id)
            : null,
          procedure_id: getCurrentProcedureId(),
          insurance_id: isConvenio ? insuranceId : null,
          insurance_plan_id: isConvenio ? insurancePlanId : null,
          appointment_type: "E",
          plan: isConvenio ? 1 : 0,
          value: 0,
          date: format(selectedDate, "dd-MM-yyyy"),
          time: selectedTime,
          observation: isConvenio
            ? "Agendado pelo totem - convênio"
            : forceParticular
            ? "Agendado pelo totem - particular após aviso de convênio não atendido"
            : "Agendado pelo totem - particular",
        });
      }

      if (response.data.success) {
        sessionStorage.removeItem("patient");
        sessionStorage.removeItem("selectedAppointment");
        sessionStorage.setItem(
          "successMessage",
          appointment ? "remarcada" : "marcada"
        );

        navigate("/success/reschedule");
      } else {
        toast.error("Erro ao concluir operação.");
      }
    } catch (error) {
      console.error("Confirmation error:", error);

      if (!appointment && appointmentPlanType === "CONVENIO" && !forceParticular) {
        setConvenioWarningMessage(
          error.response?.data?.detail ||
            "Não foi possível confirmar se este médico atende o convênio vinculado ao cadastro."
        );
        setShowConvenioWarning(true);
      } else {
        toast.error(
          error.response?.data?.detail ||
            "Erro ao concluir. Procure a recepção."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForceParticular = async () => {
    setShowConvenioWarning(false);
    setFallbackLoading(true);
    await handleConfirm(true);
    setFallbackLoading(false);
  };

  const handleChooseAnotherProfessional = () => {
    setShowConvenioWarning(false);
    setStep(2);
    setSelectedProfessional(null);
    setAvailableDates([]);
    setSelectedDate(null);
    setAvailableSlots([]);
    setSelectedTime(null);
  };

  const goBack = () => {
    if (appointment && appointmentId !== "new") {
      if (step === 4) {
        setStep(3);
        setSelectedTime(null);
        setAvailableSlots([]);
        return;
      }

      navigate("/appointments/reschedule");
      return;
    }

    if (step === 2) {
      setStep(1);
      setSelectedSpecialty(null);
      setProfessionals([]);
      setSelectedProfessional(null);
      setAvailableDates([]);
      setSelectedDate(null);
      setAvailableSlots([]);
      setSelectedTime(null);
    } else if (step === 4) {
      setStep(2);
      setAvailableDates([]);
      setSelectedDate(null);
      setAvailableSlots([]);
      setSelectedTime(null);
    } else if (step === 5) {
      setStep(4);
      setSelectedTime(null);
      setAvailableSlots([]);
    }
  };

  const getMonthDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({
      start: calendarStart,
      end: calendarEnd,
    });
  };

  const goToPreviousMonth = () => {
    const prevMonth = subMonths(currentMonth, 1);

    if (!isBefore(endOfMonth(prevMonth), startOfDay(new Date()))) {
      setCurrentMonth(prevMonth);
      fetchAvailableDatesForMonth(prevMonth);
    }
  };

  const goToNextMonth = () => {
    const nextMonth = addMonths(currentMonth, 1);
    setCurrentMonth(nextMonth);
    fetchAvailableDatesForMonth(nextMonth);
  };

  const isPastDate = (date) => {
    return isBefore(date, startOfDay(new Date()));
  };

  const isDateAvailable = (date) => {
    const dateStr = format(date, "dd-MM-yyyy");
    return availableDates.includes(dateStr);
  };

  const getStepText = () => {
    if (appointment) {
      return `Remarcação - Etapa ${step === 3 ? 1 : 2}/2`;
    }

    const stepMap = {
      1: 1,
      2: 2,
      4: 3,
      5: 4,
    };

    return `Novo Agendamento - Etapa ${stepMap[step] || 1}/4`;
  };

  if (!patient) {
    return (
      <KioskLayout showBack step="Remarcação">
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </KioskLayout>
    );
  }

  return (
    <KioskLayout showBack step={getStepText()}>
      <div className="flex-1 flex flex-col p-8 overflow-auto">
        <div className="w-full max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4 font-['Outfit'] tracking-tight">
              {step === 1 && "Selecione a Especialidade"}
              {step === 2 && "Selecione o Médico"}
              {step === 3 && "Selecione a Data"}
              {step === 4 && (appointment ? "Selecione o Horário" : "Selecione a Data")}
              {step === 5 && "Selecione o Horário"}
            </h2>

            <p className="text-xl md:text-2xl text-slate-500">
              {step === 1 && "Escolha a especialidade para marcar sua consulta"}
              {step === 2 && `${getSpecialtyName(selectedSpecialty)}`}
              {step === 3 &&
                appointment &&
                `Dr(a). ${getProfessionalName(selectedProfessional)}`}
              {step === 4 &&
                (appointment
                  ? `${format(selectedDate, "dd 'de' MMMM", {
                      locale: ptBR,
                    })} • ${getProfessionalName(selectedProfessional)}`
                  : `Dr(a). ${getProfessionalName(selectedProfessional)}`)}
              {step === 5 &&
                `${format(selectedDate, "dd 'de' MMMM", {
                  locale: ptBR,
                })} • ${getProfessionalName(selectedProfessional)}`}
            </p>
          </div>

          {step === 1 && (
            <div>
              {loadingSpecialties ? (
                <div className="text-center py-12">
                  <div className="spinner mx-auto mb-4" />
                  <p className="text-xl text-slate-500">
                    Carregando especialidades...
                  </p>
                </div>
              ) : specialties.length === 0 ? (
                <div className="text-center py-12">
                  <Stethoscope className="w-24 h-24 text-slate-300 mx-auto mb-6" />
                  <p className="text-2xl text-slate-500 mb-8">
                    Nenhuma especialidade disponível no momento.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {specialties.map((specialty, index) => (
                    <button
                      key={getSpecialtyId(specialty) || index}
                      onClick={() => handleSelectSpecialty(specialty)}
                      className="appointment-card text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-[#00357C]/10 flex items-center justify-center">
                          <Stethoscope className="w-8 h-8 text-[#00357C]" />
                        </div>

                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-slate-800">
                            {getSpecialtyName(specialty)}
                          </h3>

                          <p className="text-lg text-slate-500">
                            Especialidade
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <Button onClick={goBack} variant="outline" className="mb-6">
                <ChevronLeft className="w-5 h-5 mr-2" />
                Voltar para especialidades
              </Button>

              {loadingProfessionals ? (
                <div className="text-center py-12">
                  <div className="spinner mx-auto mb-4" />
                  <p className="text-xl text-slate-500">Carregando médicos...</p>
                </div>
              ) : professionals.length === 0 ? (
                <div className="text-center py-12">
                  <User className="w-24 h-24 text-slate-300 mx-auto mb-6" />
                  <p className="text-2xl text-slate-500 mb-8">
                    Nenhum médico disponível para esta especialidade.
                  </p>
                  <Button onClick={goBack} className="kiosk-secondary-btn">
                    Escolher outra especialidade
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {professionals.map((professional, index) => (
                    <button
                      key={getProfessionalId(professional) || index}
                      onClick={() => handleSelectProfessional(professional)}
                      className="appointment-card text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-[#00357C]/10 flex items-center justify-center">
                          <User className="w-8 h-8 text-[#00357C]" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-slate-800">
                            {getProfessionalName(professional)}
                          </h3>
                          <p className="text-lg text-slate-500">Médico</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {((appointment && step === 3) || (!appointment && step === 4)) && (
            <div>
              <Button onClick={goBack} variant="outline" className="mb-6">
                <ChevronLeft className="w-5 h-5 mr-2" />
                {appointment ? "Voltar para consultas" : "Voltar para médicos"}
              </Button>

              {loadingDates ? (
                <div className="text-center py-12">
                  <div className="spinner mx-auto mb-4" />
                  <p className="text-xl text-slate-500">Carregando datas disponíveis...</p>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-3xl p-6 max-w-4xl mx-auto">
                  <div className="flex items-center justify-between mb-6">
                    <button onClick={goToPreviousMonth} className="h-12 w-12 rounded-full bg-white hover:bg-slate-100 active:scale-95 transition-all flex items-center justify-center shadow-sm">
                      <ChevronLeft className="w-6 h-6 text-slate-600" />
                    </button>

                    <h3 className="text-2xl font-semibold text-slate-800 font-['Outfit'] capitalize">
                      {format(currentMonth, "MMMM yyyy", { locale: ptBR })}
                    </h3>

                    <button onClick={goToNextMonth} className="h-12 w-12 rounded-full bg-white hover:bg-slate-100 active:scale-95 transition-all flex items-center justify-center shadow-sm">
                      <ChevronRight className="w-6 h-6 text-slate-600" />
                    </button>
                  </div>

                  <div className="grid grid-cols-7 gap-2">
                    {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map((day) => (
                      <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                        {day}
                      </div>
                    ))}

                    {getMonthDays().map((date, index) => {
                      const past = isPastDate(date);
                      const available = isDateAvailable(date);
                      const selected = selectedDate && isSameDay(date, selectedDate);
                      const today = isToday(date);
                      const isCurrentMonth = date.getMonth() === currentMonth.getMonth();

                      return (
                        <button
                          key={index}
                          onClick={() =>
                            available &&
                            !past &&
                            isCurrentMonth &&
                            handleSelectDate(date)
                          }
                          disabled={past || !available || !isCurrentMonth}
                          className={`h-16 rounded-xl text-xl font-medium transition-all ${
                            !isCurrentMonth
                              ? "bg-transparent text-transparent cursor-default shadow-none"
                              : past || !available
                              ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                              : selected
                              ? "bg-[#00357C] text-white"
                              : today
                              ? "bg-white border-2 border-[#00357C] text-[#00357C]"
                              : available
                              ? "bg-green-100 hover:bg-green-200 text-green-700"
                              : "bg-white hover:bg-slate-100 text-slate-700"
                          }`}
                        >
                          {isCurrentMonth ? format(date, "d") : ""}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {((appointment && step === 4) || (!appointment && step === 5)) && (
            <div>
              <Button onClick={goBack} variant="outline" className="mb-6">
                <ChevronLeft className="w-5 h-5 mr-2" />
                Voltar para datas
              </Button>

              {loadingSlots ? (
                <div className="text-center py-12">
                  <div className="spinner mx-auto mb-4" />
                  <p className="text-xl text-slate-500">Carregando horários...</p>
                </div>
              ) : (
                (() => {
                  const totalVisible =
                    groupedSlots.morning.length +
                    groupedSlots.afternoon.length +
                    groupedSlots.evening.length;

                  if (totalVisible === 0) {
                    return (
                      <div className="text-center py-12">
                        <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <p className="text-xl text-slate-500">
                          Nenhum horário disponível nesta data
                        </p>
                        <Button onClick={goBack} className="mt-6 kiosk-secondary-btn">
                          Escolher outra data
                        </Button>
                      </div>
                    );
                  }

                  const renderSlotGroup = (title, slots) => {
                    if (!slots.length) return null;

                    return (
                      <div className="mb-8 last:mb-0">
                        <h4 className="text-xl font-semibold text-slate-700 mb-4">
                          {title}
                        </h4>

                        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                          {slots.map((slot, index) => {
                            const time = getSlotTime(slot);
                            const displayTime = time ? time.slice(0, 5) : "";
                            const isSelected = selectedTime === time;

                            return (
                              <button
                                key={`${title}-${displayTime}-${index}`}
                                onClick={() => setSelectedTime(time)}
                                className={`time-slot ${isSelected ? "selected" : ""}`}
                              >
                                {displayTime}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  };

                  return (
                    <>
                      <div className="bg-slate-50 rounded-3xl p-6 max-w-4xl mx-auto">
                        <h3 className="text-2xl font-semibold text-slate-800 mb-6 font-['Outfit'] flex items-center gap-3">
                          <Clock className="w-7 h-7 text-[#00357C]" />
                          Horários Disponíveis
                        </h3>

                        {renderSlotGroup("Manhã", groupedSlots.morning)}
                        {renderSlotGroup("Tarde", groupedSlots.afternoon)}
                        {renderSlotGroup("Noite", groupedSlots.evening)}
                      </div>

                      {selectedTime && (
                        <div className="mt-8 p-6 bg-white rounded-2xl border-2 border-slate-200 max-w-2xl mx-auto">
                          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="text-center md:text-left">
                              <p className="text-lg text-slate-500 mb-1">
                                {appointment ? "Nova data e horário:" : "Consulta selecionada:"}
                              </p>
                              <p className="text-2xl font-semibold text-[#00357C]">
                                {format(selectedDate, "dd 'de' MMMM", {
                                  locale: ptBR,
                                })}{" "}
                                às {selectedTime.slice(0, 5)}
                              </p>
                              <p className="text-lg text-slate-500 mt-1">
                                Com Dr(a). {getProfessionalName(selectedProfessional)}
                              </p>
                            </div>

                            <Button
                              onClick={() => handleConfirm()}
                              disabled={loading}
                              className="kiosk-primary-btn"
                            >
                              {appointment ? "Confirmar Remarcação" : "Confirmar Agendamento"}
                            </Button>
                          </div>
                        </div>
                      )}

                      {loading && (
                        <div className="text-center mt-8">
                          <div className="spinner mx-auto mb-4" />
                          <p className="text-2xl text-slate-500">
                            Confirmando agendamento...
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          )}
        </div>

        {showConvenioWarning && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl p-8 text-center">
              <h3 className="text-3xl font-bold text-slate-800 mb-4">
                Convênio não atendido por este médico
              </h3>

              <p className="text-xl text-slate-600 mb-4 leading-relaxed">
                {convenioWarningMessage}
              </p>

              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Se desejar prosseguir, o agendamento será realizado como particular
                e poderá haver cobrança no dia do atendimento.
              </p>

              <div className="flex flex-col md:flex-row gap-4">
                <Button
                  onClick={handleChooseAnotherProfessional}
                  disabled={fallbackLoading}
                  variant="outline"
                  className="flex-1 h-16 text-xl font-semibold"
                >
                  Escolher outro médico
                </Button>

                <Button
                  onClick={handleForceParticular}
                  disabled={fallbackLoading}
                  className="flex-1 h-16 text-xl font-semibold bg-[#00357C] hover:bg-[#002b5c] text-white"
                >
                  {fallbackLoading ? "Agendando..." : "Agendar particular"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </KioskLayout>
  );
}